import { useEventByHash, useGenerateReport } from "@homesite/hooks";
import type { EventDetailEvent, EventDetailProof } from "@homesite/ui";
import { EventDetailPage, useToast } from "@homesite/ui";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { env } from "../lib/env";
import { rootRoute } from "./__root";

export const eventDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/events/$hash",
	component: function EventDetail() {
		const { hash } = eventDetailRoute.useParams();
		const navigate = useNavigate();
		const { toast } = useToast();
		const { event, loading, error } = useEventByHash(hash);
		const { generate, isPending: reportPending } = useGenerateReport(
			env.VITE_API_URL,
		);

		const eventData: EventDetailEvent | null = event
			? {
					hash: event.hash,
					eventType: event.eventType,
					respaCode: event.respaCode,
					respaName: event.respaName,
					timestamp: event.timestamp,
					verified: event.verified,
					anchored: event.anchored,
					merkleRoot: event.merkleRoot ?? null,
					chainId: event.chainId != null ? String(event.chainId) : null,
					chainName: null,
				}
			: null;

		const proofData: EventDetailProof | null = event
			? {
					proof: event.proof,
					leafIndex: event.leafIndex,
					txHash: event.txHash ?? null,
					blockNumber:
						event.blockNumber != null ? Number(event.blockNumber) : null,
					blockTimestamp: event.blockTimestamp ?? null,
					polygonscanUrl: null,
				}
			: null;

		function handleBack(): void {
			navigate({ to: "/events" });
		}

		function handleCopyHash(): void {
			if (event?.hash) {
				navigator.clipboard.writeText(event.hash);
			}
		}

		function handleGenerateReport(): void {
			generate({
				format: "pdf",
				kind: "event",
				target: hash,
				dateFrom: null,
				dateTo: null,
			})
				.then(() => {
					const filename = `homesite-event-${hash.slice(0, 10)}-all.pdf`;
					toast.success("Report downloaded", { description: filename });
				})
				.catch((err: unknown) => {
					const message =
						err instanceof Error ? err.message : "Report generation failed";
					toast.error(message);
				});
		}

		return (
			<EventDetailPage
				event={eventData}
				proof={proofData}
				loading={loading}
				error={error}
				onBack={handleBack}
				onCopyHash={handleCopyHash}
				onGenerateReport={handleGenerateReport}
				reportPending={reportPending}
			/>
		);
	},
});
